import Decimal from "decimal.js";
import { roundCurrency } from "@/lib/utils/math";
import {
  formatLargeNumberInWords,
  formatLargeNumberBreakdown,
} from "@/lib/utils/numberToWords";

const MAX_SAFE = Number.MAX_SAFE_INTEGER;

export interface CompoundInterestInput {
  principal: number;
  rate: number;
  years: number;
  /** 1=annual, 4=quarterly, 12=monthly, 365=daily */
  frequency: number;
  /** Regular contribution amount (0 = none) */
  contribution?: number;
  /** Contribution frequency: 26=bi-weekly, 12=monthly, 4=quarterly, 1=annual */
  contributionFrequency?: number;
}

export interface CompoundInterestResult {
  total: number;
  interest: number;
  principal: number;
  /** Total of all contributions made (without growth) */
  totalContributions?: number;
  /** When total overflows, use this for display instead of formatCurrency(total) */
  totalDisplay?: string;
  /** When interest overflows, use this for display */
  interestDisplay?: string;
  /** When total overflows, the number written out in words (e.g. "three hundred sixty sextillion") */
  totalInWords?: string;
  /** When total overflows, breakdown in familiar terms (e.g. "that's 207,000 trillion") */
  totalBreakdown?: string;
  /** When interest overflows, the number written out in words */
  interestInWords?: string;
  /** When interest overflows, breakdown in familiar terms */
  interestBreakdown?: string;
  chartData: { year: number; total: number; interest: number }[];
}

function formatLargeDecimal(d: Decimal): string {
  const exp = d.toExponential(2);
  return `$${exp} (approx)`;
}

/** FV with contributions; contributionFreq can differ from compounding frequency */
function futureValueWithContributions(
  p: Decimal,
  pmt: Decimal,
  r: Decimal,
  n: Decimal,
  t: Decimal,
  compoundFreq: number,
  contributionFreq: number
): Decimal {
  if (pmt.lte(0)) {
    return p.mul(new Decimal(1).add(r.div(n)).pow(n.mul(t)));
  }

  const numContributions = contributionFreq * t.toNumber();
  let balance = p;
  const periodsPerContribution = compoundFreq / contributionFreq;
  const factor = new Decimal(1).add(r.div(n)).pow(periodsPerContribution);

  for (let j = 0; j < numContributions; j++) {
    balance = balance.mul(factor).add(pmt);
  }
  return balance;
}

export function calculateCompoundInterest(
  input: CompoundInterestInput
): CompoundInterestResult {
  const {
    principal,
    rate,
    years,
    frequency,
    contribution = 0,
    contributionFrequency = 12,
  } = input;
  const r = new Decimal(rate).div(100);
  const n = new Decimal(frequency);
  const t = new Decimal(years);
  const p = new Decimal(principal);
  const pmt = new Decimal(contribution);

  const totalDecimal =
    contribution <= 0
      ? p.mul(new Decimal(1).add(r.div(n)).pow(n.mul(t)))
      : futureValueWithContributions(
          p,
          pmt,
          r,
          n,
          t,
          frequency,
          contributionFrequency
        );
  const overflow = totalDecimal.gte(MAX_SAFE);

  const total = overflow ? 0 : roundCurrency(totalDecimal);
  const totalContributions =
    contribution > 0 ? contribution * contributionFrequency * years : 0;
  const interestDecimal = totalDecimal.minus(p).minus(totalContributions);
  const interest = overflow ? 0 : roundCurrency(interestDecimal);

  const chartData: { year: number; total: number; interest: number }[] = [];
  for (let y = 0; y <= years; y++) {
    const yearT = new Decimal(y);
    const yearTotalDecimal =
      contribution <= 0
        ? p.mul(new Decimal(1).add(r.div(n)).pow(n.mul(yearT)))
        : futureValueWithContributions(
            p,
            pmt,
            r,
            n,
            yearT,
            frequency,
            contributionFrequency
          );
    const yearTotal = yearTotalDecimal.gte(MAX_SAFE)
      ? MAX_SAFE
      : roundCurrency(yearTotalDecimal);
    const yearContributions =
      contribution > 0 ? contribution * contributionFrequency * y : 0;
    const yearInterest = roundCurrency(
      new Decimal(yearTotal).minus(principal).minus(yearContributions)
    );
    chartData.push({
      year: y,
      total: yearTotal,
      interest: yearInterest,
    });
  }

  return {
    total,
    interest,
    principal,
    totalContributions: contribution > 0 ? totalContributions : undefined,
    totalDisplay: overflow ? formatLargeDecimal(totalDecimal) : undefined,
    interestDisplay: overflow ? formatLargeDecimal(interestDecimal) : undefined,
    totalInWords: overflow
      ? formatLargeNumberInWords(totalDecimal.toExponential(2))
      : undefined,
    totalBreakdown: overflow
      ? formatLargeNumberBreakdown(totalDecimal.toExponential(2))
      : undefined,
    interestInWords: overflow
      ? formatLargeNumberInWords(interestDecimal.toExponential(2))
      : undefined,
    interestBreakdown: overflow
      ? formatLargeNumberBreakdown(interestDecimal.toExponential(2))
      : undefined,
    chartData,
  };
}
